import {
  MarketingLanguageType,
  type EmailBlockTypeType,
  type EmailTemplateInputType,
} from "@/types/graphql/partner.generated";
import type { EmailTemplateDetails } from "@/partner/entities/marketing";
import { blockIssues, newBlock, type EditorBlock } from "./blocks";

export interface EditorVariant {
  language: MarketingLanguageType;
  subject: string;
  preheader: string;
  blocks: EditorBlock[];
}

export interface EditorState {
  name: string;
  variants: EditorVariant[];
  activeLanguage: MarketingLanguageType;
}

export type EditorAction =
  | { type: "setName"; name: string }
  | { type: "addVariant"; language: MarketingLanguageType }
  | { type: "removeVariant"; language: MarketingLanguageType }
  | { type: "setActiveLanguage"; language: MarketingLanguageType }
  | { type: "setSubject"; subject: string }
  | { type: "setPreheader"; preheader: string }
  | { type: "addBlock"; blockType: EmailBlockTypeType }
  | { type: "updateBlock"; id: string; patch: Partial<EditorBlock> }
  | { type: "removeBlock"; id: string }
  | { type: "duplicateBlock"; id: string }
  | { type: "moveBlock"; from: number; to: number };

export const emptyEditorState = (
  language: MarketingLanguageType
): EditorState => ({
  name: "",
  variants: [{ language, subject: "", preheader: "", blocks: [] }],
  activeLanguage: language,
});

export const activeVariant = (state: EditorState): EditorVariant =>
  state.variants.find((v) => v.language === state.activeLanguage) ??
  state.variants[0];

const mapActive = (
  state: EditorState,
  fn: (v: EditorVariant) => EditorVariant
): EditorState => {
  const language = activeVariant(state).language;
  return {
    ...state,
    variants: state.variants.map((v) => (v.language === language ? fn(v) : v)),
  };
};

export function editorReducer(
  state: EditorState,
  action: EditorAction
): EditorState {
  switch (action.type) {
    case "setName":
      return { ...state, name: action.name };
    case "addVariant": {
      if (state.variants.some((v) => v.language === action.language)) {
        return { ...state, activeLanguage: action.language };
      }
      // Новый язык начинается с копии текущего: переводить проще, чем
      // собирать письмо заново. id блоков новые, они уникальны в варианте.
      const source = activeVariant(state);
      const copy: EditorVariant = {
        ...source,
        language: action.language,
        blocks: source.blocks.map((b) => ({ ...b, id: crypto.randomUUID() })),
      };
      return {
        ...state,
        variants: [...state.variants, copy],
        activeLanguage: action.language,
      };
    }
    case "removeVariant": {
      if (state.variants.length <= 1) return state;
      const variants = state.variants.filter(
        (v) => v.language !== action.language
      );
      const activeLanguage = variants.some(
        (v) => v.language === state.activeLanguage
      )
        ? state.activeLanguage
        : variants[0].language;
      return { ...state, variants, activeLanguage };
    }
    case "setActiveLanguage":
      return { ...state, activeLanguage: action.language };
    case "setSubject":
      return mapActive(state, (v) => ({ ...v, subject: action.subject }));
    case "setPreheader":
      return mapActive(state, (v) => ({ ...v, preheader: action.preheader }));
    case "addBlock":
      return mapActive(state, (v) => ({
        ...v,
        blocks: [...v.blocks, newBlock(action.blockType)],
      }));
    case "updateBlock":
      return mapActive(state, (v) => ({
        ...v,
        blocks: v.blocks.map((b) =>
          b.id === action.id ? { ...b, ...action.patch, id: b.id } : b
        ),
      }));
    case "removeBlock":
      return mapActive(state, (v) => ({
        ...v,
        blocks: v.blocks.filter((b) => b.id !== action.id),
      }));
    case "duplicateBlock":
      return mapActive(state, (v) => {
        const index = v.blocks.findIndex((b) => b.id === action.id);
        if (index < 0) return v;
        const blocks = [...v.blocks];
        blocks.splice(index + 1, 0, {
          ...v.blocks[index],
          id: crypto.randomUUID(),
        });
        return { ...v, blocks };
      });
    case "moveBlock":
      return mapActive(state, (v) => {
        const blocks = [...v.blocks];
        const [moved] = blocks.splice(action.from, 1);
        if (!moved) return v;
        blocks.splice(action.to, 0, moved);
        return { ...v, blocks };
      });
    default:
      return state;
  }
}

export const toEmailTemplateInput = (
  state: EditorState
): EmailTemplateInputType => ({
  name: state.name.trim(),
  variants: state.variants.map((v) => ({
    language: v.language,
    subject: v.subject,
    preheader: v.preheader,
    blocks: v.blocks,
  })),
});

/**
 * Загруженное письмо в состояние редактора. `imageUrl` не входит в
 * `EmailBlockInput` (сервер отдаёт его для показа), `__typename` тоже
 * отбрасываем: иначе мутация получит лишние поля инпута.
 */
export const fromEmailTemplate = (
  template: EmailTemplateDetails
): EditorState => ({
  name: template.name,
  variants: template.variants.map((v) => ({
    language: v.language,
    subject: v.subject,
    preheader: v.preheader,
    blocks: v.blocks.map(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ({ __typename, imageUrl, required, ...rest }) => ({
        ...rest,
        required: Boolean(required),
      })
    ),
  })),
  activeLanguage:
    template.variants[0]?.language ?? MarketingLanguageType.EnType,
});

/**
 * Вариант готов к превью и сохранению: есть тема и у блоков нет проблем
 * (те же проверки, что у EmailTemplateValidator бэкенда). urlVariables:
 * переменные-адреса кнопок с сервера (marketingLimits.emailUrlVariables).
 */
export const variantIsValid = (
  variant: EditorVariant,
  urlVariables: readonly string[] = []
): boolean =>
  Boolean(variant.subject.trim()) &&
  variant.blocks.every((b) => blockIssues(b, urlVariables) === null);

/** Черновик можно сохранять: есть название и все варианты валидны. */
export const canSave = (
  state: EditorState,
  urlVariables: readonly string[] = []
): boolean =>
  Boolean(state.name.trim()) &&
  state.variants.every((variant) => variantIsValid(variant, urlVariables));
